import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["requiredRowProps", "row", "trRef"];
import { useCallback, useMemo } from 'react';
import { useTablePassThroughProps } from 'framework-listing-lib/internal/FrameworkDataTable/providers/FrameworkDataTablePassThroughPropsProvider';
import AsyncPqlTableRow from 'framework-listing-lib/internal/Table/components/AsyncPqlTableRow';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { ROW_SIZE_MEDIUM, ROW_SIZE_SMALL } from 'framework-listing-lib/constants/sizes';
import { useGetIsRowInvalid } from 'framework-listing-lib/internal/FrameworkDataTable/hooks/useInlineEdit';
import ListingLibExpandableContent from 'framework-listing-lib/internal/Table/components/ListingLibExpandableContent';
import { useListingLibBulkActions } from 'framework-listing-lib/internal/Table/providers/ListingLibBulkActionsProvider';
import { useHasInlineEdit } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import useFolderProps from 'framework-listing-lib/internal/Folders/hooks/useFolderProps';
import DraggableTableRowV2 from 'framework-listing-lib/internal/Table/components/DraggableTableRowV2';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function ListingLibRowComponent(_ref) {
  let {
      requiredRowProps,
      row,
      trRef
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const hasInlineEdit = useHasInlineEdit();
  const isFoldersMode = useIsFoldersMode();
  const {
    isReadOnly
  } = useFolderProps();
  const {
    data,
    renderNestedContent,
    renderPqlRow,
    rowProps,
    tableColumns,
    columns
  } = useTablePassThroughProps();
  const {
    getIsSelected,
    getNestedLevel,
    getParentRow,
    hasBulkActions,
    selectedIds,
    selectionMode
  } = useListingLibBulkActions();
  const rowIndex = requiredRowProps['data-observer-id'];
  const getIsRowInvalid = useGetIsRowInvalid();
  const isRowSelected = getIsSelected(row.objectId);
  const rowStyle = useMemo(() => {
    if (!rowProps || !rowProps.rowSize) {
      return {};
    }
    switch (rowProps.rowSize) {
      default:
      case 'extra-small':
        return {};
      case 'small':
        return {
          height: ROW_SIZE_SMALL
        };
      case 'medium':
        return {
          height: ROW_SIZE_MEDIUM
        };
    }
  }, [rowProps]);
  const handleRowClick = useCallback(event => {
    const eventTarget = event.target;

    /**
     * If @eventTarget is nested element within a hover-cell, we don't propagate row clicks.
     * This is to enable users to select the "hover" action within the cell.
     */
    const isClickingHoverCell = Boolean(eventTarget.closest(`td[data-test-id="crm-table-cell"][data-test-hover-cell="true"]`));

    /**
     * Don't propagate row clicks from bulk action (checkbox) cells.
     * For teams that open a side panel on row click, the checkbox is on the way.
     */
    const isClickingCheckboxCell = Boolean(eventTarget.closest(`[data-test-id^="checkbox-select-row-"]`)) || Boolean(eventTarget.closest(`[class^="SelectRowCheckboxCell"]`));

    /**
     * Clicks in hover actions' dropdown menu items also trigger row clicks.
     * Since they don't belong to the table row, we should also ignore those events.
     */
    const isChildOfTableRow = Boolean(eventTarget.closest(`tr[data-test-id="crm-table-row"]`));
    if (isClickingHoverCell || isClickingCheckboxCell || !isChildOfTableRow) {
      return;
    }

    /**
     * When inlineEditing is enabled, we also ignore row clicks.
     * This is to prevent side panels (or any other interaction) from being opened while a cell is being inline edited.
     */
    if (hasInlineEdit || !rowProps || !rowProps.onClick) {
      return;
    }
    rowProps.onClick({
      crmObject: row,
      parentCrmObject: getParentRow(row.objectId)
    });
  }, [getParentRow, hasInlineEdit, row, rowProps]);
  const invalidProps = useMemo(() => getIsRowInvalid(String(row.objectId)) ? {
    'aria-invalid': true
  } : {}, [getIsRowInvalid, row.objectId]);
  const rowNestedLevel = getNestedLevel(row.objectId);
  const trProps = useMemo(() => Object.assign({
    ref: trRef
  }, requiredRowProps, rest, invalidProps, {
    'data-test-id': 'crm-table-row',
    'data-test-object-id': row.objectId,
    'data-test-nested-level': rowNestedLevel,
    onClick: handleRowClick,
    style: rowStyle
  }), [handleRowClick, invalidProps, requiredRowProps, rest, row.objectId, rowNestedLevel, rowStyle, trRef]);
  const sanitizedRowProps = useMemo(() => {
    const props = Object.assign({}, trProps);
    delete props.selectable;
    delete props.rowId;
    return props;
  }, [trProps]);
  if (isFoldersMode && !isReadOnly) {
    return /*#__PURE__*/_jsx(DraggableTableRowV2, {
      crmObject: row,
      isSelected: isRowSelected,
      rowProps: sanitizedRowProps,
      selectedIds: selectedIds,
      selectionMode: selectionMode
    });
  }
  const CustomRowComponent = rowProps && rowProps.RowComponent ? rowProps.RowComponent : undefined;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderPqlRow && /*#__PURE__*/_jsx(AsyncPqlTableRow, {
      hasBulkActions: hasBulkActions,
      isFrameworkDataTable: true,
      renderPqlRow: renderPqlRow,
      rowIndex: rowIndex
    }), CustomRowComponent ? /*#__PURE__*/_jsx(CustomRowComponent, {
      crmObject: row,
      trProps: trProps
    }) : /*#__PURE__*/_jsx("tr", Object.assign({}, sanitizedRowProps)), /*#__PURE__*/_jsx(ListingLibExpandableContent, {
      crmObject: row,
      isParentRowSelected: isRowSelected,
      nestedLevel: rowNestedLevel,
      renderNestedContent: renderNestedContent,
      tableColumns: tableColumns,
      tableRowProps: {
        columns,
        crmObject: row,
        crmObjects: data ? data.results : [],
        isNested: false,
        isSelected: false,
        nestedLevel: rowNestedLevel,
        renderNestedContent,
        rowIndex,
        rowProps
      }
    })]
  });
}